// src/pages/PrayerTimes.tsx
import { useEffect, useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import {
  MapPin,
  SunHorizon,
  Sun,
  CloudSun,
  CloudMoon,
  Moon,
  Watch,
} from "@phosphor-icons/react";
import { usePrayerMode } from "../providers/PrayerModeProvider";
import {
  getPrayerDay,
  type PrayerTimes,
  type PrayerDayInfo,
} from "../utils/getPrayerTimes";
import PageTitle from "../components/ui/PageTitle";

type ShiaKey = "Fajr" | "Shorooq" | "Dhuhr" | "Maghrib";
type SunniKey = "Fajr" | "Shorooq" | "Dhuhr" | "Asr" | "Maghrib" | "Isha";
type AnyKey = ShiaKey | SunniKey;
type Dir = "rtl" | "ltr";

const ORDER_SHIA: ShiaKey[] = ["Fajr", "Shorooq", "Dhuhr", "Maghrib"];
const ORDER_SUNNI: SunniKey[] = [
  "Fajr",
  "Shorooq",
  "Dhuhr",
  "Asr",
  "Maghrib",
  "Isha",
];

const LABELS_SHIA: Record<ShiaKey, { ar: string; en: string }> = {
  Fajr: { ar: "الفجر", en: "Fajr" },
  Shorooq: { ar: "الشروق", en: "Sunrise" },
  Dhuhr: { ar: "الظهرين", en: "Dhuhrayn" },
  Maghrib: { ar: "العشائين", en: "Maghribayn" },
};
const LABELS_SUNNI: Record<SunniKey, { ar: string; en: string }> = {
  Fajr: { ar: "الفجر", en: "Fajr" },
  Shorooq: { ar: "الشروق", en: "Sunrise" },
  Dhuhr: { ar: "الظهر", en: "Dhuhr" },
  Asr: { ar: "العصر", en: "Asr" },
  Maghrib: { ar: "المغرب", en: "Maghrib" },
  Isha: { ar: "العشاء", en: "Isha" },
};

const ICONS: Record<AnyKey, React.ElementType> = {
  Fajr: Moon,
  Shorooq: SunHorizon,
  Dhuhr: Sun,
  Asr: CloudSun,
  Maghrib: CloudMoon,
  Isha: Moon,
};

export default function PrayerTimes() {
  const { mode, setMode } = usePrayerMode();
  const theme = useTheme() as { dir?: Dir };
  const dir: Dir =
    theme?.dir ??
    ((document?.documentElement?.getAttribute("dir") as Dir) || "rtl");

  const [times, setTimes] = useState<PrayerTimes | null>(null);
  const [dayInfo, setDayInfo] = useState<PrayerDayInfo | null>(null);
  const [nextPrayer, setNextPrayer] = useState<AnyKey | null>(null);

  useEffect(() => {
    (async () => {
      const { times, info } = await getPrayerDay({ mode });
      setTimes(times);
      setDayInfo(info);

      const ORDER = mode === "shia" ? ORDER_SHIA : ORDER_SUNNI;
      const toMin = (hhmm?: string) => {
        if (typeof hhmm !== "string") return NaN;
        const m = hhmm.match(/^(\d{1,2}):(\d{2})$/);
        if (!m) return NaN;
        return Number(m[1]) * 60 + Number(m[2]);
      };
      const now = new Date();
      const current = now.getHours() * 60 + now.getMinutes();

      const list = ORDER.map((k) => {
        const t = (times as any)[k] as string | undefined;
        return { k, m: toMin(t) };
      }).filter((x) => !Number.isNaN(x.m));

      let up: AnyKey | null =
        (list.find((x) => x.m > current)?.k as AnyKey) ?? null;
      if (!up) up = ORDER[0] as AnyKey;
      setNextPrayer(up);
    })();
  }, [mode]);

  const LABELS = useMemo(() => {
    const base = mode === "shia" ? LABELS_SHIA : LABELS_SUNNI;
    return Object.fromEntries(
      Object.entries(base).map(([k, v]) => [k, dir === "rtl" ? v.ar : v.en])
    ) as Record<AnyKey, string>;
  }, [mode, dir]);

  return (
    <Wrap>
      <PageTitle
        icon={<Watch size={24} weight="duotone" color="var(--primary)" />}
        center
        gap={8}
        as="h1"
      >
        {dir === "rtl" ? "مواقيت الصلاة" : "Prayer Times"}
      </PageTitle>

      <Row>
        <ModeSwitch>
          <button
            data-active={mode === "shia" ? "" : undefined}
            onClick={() => setMode("shia")}
          >
            شيعي
          </button>
          <button
            data-active={mode === "sunni" ? "" : undefined}
            onClick={() => setMode("sunni")}
          >
            سني
          </button>
        </ModeSwitch>

        <Loc>
          <MapPin size={16} weight="bold" color="var(--primary)" />
          <span>{dir === "rtl" ? "المنامة، البحرين" : "Manama, Bahrain"}</span>
        </Loc>
      </Row>

      {dayInfo && (
        <DayCard>
          {dayInfo.dayName && (
            <p>
              {dir === "rtl" ? "اليوم:" : "Day:"}{" "}
              <strong>{dayInfo.dayName}</strong>
            </p>
          )}
          {dayInfo.hijri && (
            <p>
              {dir === "rtl" ? "التاريخ الهجري:" : "Hijri Date:"}{" "}
              <strong>{dayInfo.hijri}</strong>
            </p>
          )}
          {dayInfo.ahkam && <Tag>أحكام قص الشعر والأظافر: {dayInfo.ahkam}</Tag>}
          {dayInfo.monasabat && (
            <Tag $purple>مناسبات اليوم: {dayInfo.monasabat}</Tag>
          )}
          {dayInfo.layali !== undefined && (
            <Tag $green={dayInfo.layali} $red={!dayInfo.layali}>
              {dayInfo.layali
                ? dir === "rtl"
                  ? "ليلة مباركة"
                  : "Blessed Night"
                : dir === "rtl"
                ? "ليلة نحس"
                : "Unlucky Night"}
            </Tag>
          )}
        </DayCard>
      )}

      {nextPrayer && (
        <NextChip>
          {dir === "rtl" ? "الصلاة القادمة:" : "Next:"}{" "}
          <strong>{LABELS[nextPrayer]}</strong>
        </NextChip>
      )}

      <List>
        {(mode === "shia" ? ORDER_SHIA : ORDER_SUNNI).map((key) => {
          const Icon = ICONS[key];
          return (
            <Item
              key={key}
              data-highlight={key === nextPrayer ? "" : undefined}
            >
              <Left>
                <Icon size={20} weight="duotone" color="var(--primary)" />
                <span>{LABELS[key]}</span>
              </Left>
              <strong>{(times as any)?.[key] ?? "--:--"}</strong>
            </Item>
          );
        })}
      </List>
    </Wrap>
  );
}

const Wrap = styled.div`
  padding: 24px 16px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const ModeSwitch = styled.div`
  display: inline-flex;
  border: 1px solid var(--border);
  border-radius: 999px;
  overflow: hidden;
  background: var(--surface);

  button {
    padding: 6px 12px;
    border: 0;
    background: transparent;
    cursor: pointer;
    font-weight: 600;
    color: var(--text);
  }
  button[data-active] {
    background: var(--primary);
    color: #fff;
  }
`;

const Loc = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
`;

const DayCard = styled.div`
  background: var(--surface);
  padding: 12px;
  border-radius: 12px;
  box-shadow: var(--shadow);
  width: 100%;
  max-width: 520px;
  p {
    margin: 4px 0;
    text-align: center;
    color: var(--text);
  }
`;

const Tag = styled.div<{ $purple?: boolean; $green?: boolean; $red?: boolean }>`
  margin-top: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  text-align: center;
  font-size: 0.9rem;
  background: ${({ $purple, $green, $red }) =>
    $purple
      ? "rgba(168,85,247,.12)"
      : $green
      ? "rgba(34,197,94,.12)"
      : $red
      ? "rgba(239,68,68,.12)"
      : "rgba(0,0,0,.05)"};
  color: ${({ $purple, $green, $red }) =>
    $purple
      ? "#a855f7"
      : $green
      ? "#22c55e"
      : $red
      ? "#ef4444"
      : "var(--text)"};
`;

const NextChip = styled.div`
  padding: 6px 10px;
  border-radius: 999px;
  background: color-mix(in oklab, var(--primary) 12%, var(--surface));
  border: 1px solid var(--border);
  color: var(--text);
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px;
  border-radius: 12px;
  background: var(--background);
  border: 1px solid var(--border);

  &[data-highlight] {
    border: 2px solid var(--primary);
    box-shadow: 0 0 0 4px color-mix(in oklab, var(--primary) 20%, transparent);
  }

  strong {
    font-size: 1.1rem;
    color: var(--text);
  }
`;

const Left = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;
